/**
 * Column definitions for the reports table.
 *
 * Mirrors the journal-articles columns, keeping only the fields reports
 * actually carry. Article-only fields (journal, volume, issue, DOI, PubMed,
 * abstract, topics, collections, PR score, location fields) are excluded, and
 * a "Report Type" column is added. Keys are `JournalArticle` field names plus
 * `reportType`; labels match the article columns where the field is the same.
 */

import { ColumnGroup } from "@/components/shared/ManageColumnsDialog"
import { ViewMode } from "@/components/shared/TableViewSelector"

/**
 * Column groups for the compact and expanded views.
 * These views share the same set of columns.
 */
const COMPACT_COLUMN_GROUPS: ColumnGroup<string>[] = [
  { keys: ["sourceId"], label: "Source ID" },
  { keys: ["title"], label: "Title" },
  { keys: ["reportType"], label: "Report Type" },
  { keys: ["publicationYear"], label: "Year" },
  { keys: ["institutions"], label: "Institutions" },
  { keys: ["reportNumber"], label: "Report Number" },
  { keys: ["mapSurveyId"], label: "MAP Survey ID" },
  { keys: ["surveyType"], label: "Survey Type" },
]

/**
 * Column groups for the extraction status view.
 */
const STATUS_COLUMN_GROUPS: ColumnGroup<string>[] = [
  { keys: ["sourceId"], label: "Source ID" },
  { keys: ["title"], label: "Title" },
  { keys: ["reportType"], label: "Report Type" },
  { keys: ["publicationYear"], label: "Year" },
  { keys: ["prExtractionStatus"], label: "PR Extraction Status" },
  { keys: ["rsExtractionStatus"], label: "RS Extraction Status" },
]

/**
 * Column groups for the full view.
 * Shows every report-relevant column individually: the compact columns plus
 * the remaining report metadata, audit fields, and extraction statuses.
 */
const FULL_COLUMN_GROUPS: ColumnGroup<string>[] = [
  { keys: ["sourceId"], label: "Source ID" },
  { keys: ["title"], label: "Title" },
  { keys: ["reportType"], label: "Report Type" },
  { keys: ["authors"], label: "Authors" },
  { keys: ["publicationYear"], label: "Year" },
  { keys: ["pages"], label: "Pages" },
  { keys: ["institutions"], label: "Institutions" },
  { keys: ["reportNumber"], label: "Report Number" },
  { keys: ["ihmeNid"], label: "IHME NID" },
  { keys: ["mapSurveyId"], label: "MAP Survey ID" },
  { keys: ["surveyType"], label: "Survey Type" },
  { keys: ["citation"], label: "Citation" },
  { keys: ["suggestedCitation"], label: "Suggested Citation" },
  { keys: ["url"], label: "URL" },
  { keys: ["city"], label: "City" },
  { keys: ["researchNotes"], label: "Research Notes" },
  { keys: ["usagePermission"], label: "Usage Permission" },
  { keys: ["dateUrlVisited"], label: "Date URL Visited" },
  { keys: ["createdBy"], label: "Created By" },
  { keys: ["createdDate"], label: "Created Date" },
  { keys: ["lastModifiedBy"], label: "Last Modified By" },
  { keys: ["lastModifiedDate"], label: "Last Modified Date" },
  { keys: ["prExtractionStatus"], label: "PR Extraction Status" },
  { keys: ["rsExtractionStatus"], label: "RS Extraction Status" },
]

/**
 * Returns the column groups appropriate for the given view mode.
 *
 * The reports table offers the compact (also used for expanded), status, and
 * full views. The article-only "pr-bulk-review" view has no report column set,
 * so asking for it throws rather than silently falling back to another view.
 *
 * @param viewMode - The currently active view mode.
 * @returns Array of column groups for the Manage Columns dialog.
 * @throws Error when `viewMode` is not a view the reports table supports.
 */
export function getColumnGroupsForViewMode(viewMode: ViewMode): ColumnGroup<string>[] {
  switch (viewMode) {
    case "compact":
    case "expanded":
      return COMPACT_COLUMN_GROUPS
    case "status":
      return STATUS_COLUMN_GROUPS
    case "full":
      return FULL_COLUMN_GROUPS
    default:
      throw new Error(`Unsupported view mode for the reports table: ${viewMode}`)
  }
}

/** All column keys across all views (used for default visibility state). */
export const ALL_REPORT_COLUMN_KEYS: Set<string> = new Set(
  FULL_COLUMN_GROUPS.flatMap((g) => g.keys)
)
